import { Fragment } from 'react';
import { Check, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWizard } from '../engine';
import type { WizardStepConfig } from '../engine/types';

interface WizardStepperProps {
  className?: string;
}

export function WizardStepper({ className }: WizardStepperProps) {
  const {
    visibleSteps,
    currentStepIndex,
    isStepComplete,
    isStepAccessible,
    goTo,
  } = useWizard();

  const handleStepClick = (step: WizardStepConfig, _index: number) => {
    if (isStepAccessible(step.id)) {
      goTo(step.id);
    }
  };

  return (
    <div
      className={cn(
        'relative w-full rounded-lg border border-gray-200 bg-white shadow-sm py-4 transition-all duration-300',
        className
      )}
    >
      {/* Steps + bars only between steps (nothing to the left of the first) */}
      <div className="flex items-start overflow-x-auto px-4 pb-1 pt-2">
        {visibleSteps.map((step, index) => {
          const isCompleted = isStepComplete(step.id);
          const isCurrent = index === currentStepIndex;
          const isAccessible = isStepAccessible(step.id);
          const isPast = index < currentStepIndex;
          const isFuture = index > currentStepIndex;
          const isLast = index === visibleSteps.length - 1;

          return (
            <Fragment key={step.id}>
              <div className="flex shrink-0 flex-col items-center" style={{ minWidth: '4.5rem' }}>
                <StepIndicator
                  step={step}
                  index={index}
                  isCompleted={isCompleted}
                  isCurrent={isCurrent}
                  isAccessible={isAccessible}
                  isPast={isPast}
                  isFuture={isFuture}
                  onClick={() => handleStepClick(step, index)}
                />
                <span
                  title={step.title}
                  className={cn(
                    'mt-2 block w-full min-h-[2rem] line-clamp-2 px-0.5 text-center text-xs font-medium leading-tight',
                    isCurrent && 'text-blue-600',
                    isCompleted && !isCurrent && 'text-green-600',
                    isFuture && !isAccessible && 'text-gray-400',
                    isFuture && isAccessible && 'text-gray-500',
                    isPast && !isCompleted && 'text-gray-500'
                  )}
                >
                  {step.title}
                </span>
              </div>
              {!isLast && (
                <div className="flex h-9 min-w-[1rem] flex-1 items-center px-0.5" aria-hidden>
                  <div className="relative h-0.5 w-full flex-1 rounded-full bg-gray-200">
                    <div
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-full bg-blue-500 transition-all duration-500',
                        currentStepIndex > index ? 'w-full' : 'w-0'
                      )}
                    />
                  </div>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// STEP INDICATOR (circle only; the title is always shown in the parent)
// ============================================================================

interface StepIndicatorProps {
  step: WizardStepConfig;
  index: number;
  isCompleted: boolean;
  isCurrent: boolean;
  isAccessible: boolean;
  isPast: boolean;
  isFuture: boolean;
  onClick: () => void;
}

function StepIndicator({
  step: _step,
  index,
  isCompleted,
  isCurrent,
  isAccessible,
  isPast,
  isFuture,
  onClick,
}: StepIndicatorProps) {
  const canClick = isAccessible && !isCurrent;

  return (
    <div
      className={cn(
        'group flex shrink-0 flex-col items-center transition-all duration-300',
        canClick && 'cursor-pointer',
        !isAccessible && !isCurrent && 'cursor-not-allowed'
      )}
      onClick={canClick ? onClick : undefined}
    >
      <div
        className={cn(
          'relative z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 bg-white transition-all duration-300',
          isCurrent && 'border-blue-500 ring-4 ring-blue-100',
          isCompleted && !isCurrent && 'border-green-500 bg-green-500',
          isPast && !isCompleted && 'border-gray-300',
          isFuture && !isAccessible && 'border-gray-200',
          isFuture && isAccessible && 'border-gray-300',
          canClick && 'hover:border-blue-400 hover:ring-2 hover:ring-blue-100'
        )}
      >
        {isCompleted && !isCurrent ? (
          <Check className="h-4 w-4 text-white" />
        ) : isFuture && !isAccessible ? (
          <Lock className="h-4 w-4 text-gray-300" />
        ) : (
          <span
            className={cn(
              'text-sm font-semibold',
              isCurrent && 'text-blue-600',
              isPast && !isCompleted && 'text-gray-400',
              isFuture && 'text-gray-400'
            )}
          >
            {index + 1}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPACT VARIANT (Alternative)
// ============================================================================

interface WizardStepperCompactProps {
  className?: string;
}

/**
 * Ultra-compact stepper showing only dots.
 * Use this for mobile or very limited space.
 */
export function WizardStepperCompact({ className }: WizardStepperCompactProps) {
  const { visibleSteps, currentStepIndex, isStepComplete, currentStep } =
    useWizard();

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      {/* Current step title */}
      <span className="text-sm font-medium text-gray-700">
        {currentStep.title}
      </span>

      {/* Dots */}
      <div className="flex items-center gap-1.5">
        {visibleSteps.map((step, index) => {
          const isCompleted = isStepComplete(step.id);
          const isCurrent = index === currentStepIndex;

          return (
            <div
              key={step.id}
              className={cn(
                'rounded-full transition-all duration-300',
                isCurrent
                  ? 'h-2.5 w-2.5 bg-blue-500'
                  : isCompleted
                    ? 'h-2 w-2 bg-green-500'
                    : 'h-2 w-2 bg-gray-300'
              )}
            />
          );
        })}
      </div>

      {/* Progress text */}
      <span className="text-xs text-gray-500">
        Paso {currentStepIndex + 1} de {visibleSteps.length}
      </span>
    </div>
  );
}
